import { Checkmark } from "react-checkmark";
import { Spinner } from "../components/Spinner";
import { useDatachainXdcPay } from "../hooks/useXdcPay";
import { getImageUrl, getExplorerUrl } from "../utils/utils";
import React, { useState } from "react";
import { ResponseError } from "../models";
import { useGet } from "../hooks/useGet";
import { Constants } from "../constants";

export const XdcPaymentGateway = ({ cartTotal }: { cartTotal: string }) => {
    const { pay, txId, addDcErc20Token, isXdcPayAvailable, getAmountWithoutDecimals } = useDatachainXdcPay();
    return (
        <div className="flex flex-col justify-center items-center">
            <Loading
                cardTotal={cartTotal}
                txId={txId}
                pay={pay}
                addDcErc20Token={addDcErc20Token}
                isXdcPayAvailable={isXdcPayAvailable}
                getAmountWithoutDecimals={getAmountWithoutDecimals}
            />
        </div>
    );
};

const Loading = ({
    isXdcPayAvailable,
    pay,
    txId,
    addDcErc20Token,
    cardTotal,
    getAmountWithoutDecimals
}: Gateway & { isXdcPayAvailable: boolean | undefined }) => {
    const { data: rate } = useGet<{ rate: number } & ResponseError>("/rate", {
        restRoot: window.location.origin,
        restNamespace: Constants.rest.prefix
    });

    const { data: receiveAddress } = useGet<{ receiveAddress: string } & ResponseError>("/receive-address", {
        restRoot: window.location.origin,
        restNamespace: Constants.rest.prefix
    });
    return isXdcPayAvailable === undefined || !rate || !receiveAddress ? (
        <Spinner />
    ) : (
        <GatewayContent
            cardTotal={cardTotal}
            txId={txId}
            pay={pay}
            addDcErc20Token={addDcErc20Token}
            isXdcPayAvailable={isXdcPayAvailable}
            rate={rate?.rate}
            getAmountWithoutDecimals={getAmountWithoutDecimals}
            receiveAddress={receiveAddress.receiveAddress}
        />
    );
};

const GatewayContent = ({
    isXdcPayAvailable,
    pay,
    txId,
    addDcErc20Token,
    cardTotal,
    rate,
    getAmountWithoutDecimals,
    receiveAddress
}: Gateway & { isXdcPayAvailable: boolean }) =>
    isXdcPayAvailable === true ? (
        <PaymentGateway
            getAmountWithoutDecimals={getAmountWithoutDecimals}
            rate={rate}
            cardTotal={cardTotal}
            txId={txId}
            pay={pay}
            addDcErc20Token={addDcErc20Token}
            receiveAddress={receiveAddress}
        />
    ) : (
        <InstallXdcPay />
    );

interface Gateway {
    txId: string;
    pay: (amount: string, rate: number, receiveAddress: string) => Promise<{ message: string; status: string }>;
    addDcErc20Token: () => {};
    cardTotal: string;
    rate?: number;
    getAmountWithoutDecimals: (amount: string, rate: number) => string;
    receiveAddress?: string;
}

const PaymentGateway = ({
    txId,
    pay,
    addDcErc20Token,
    cardTotal,
    rate,
    getAmountWithoutDecimals,
    receiveAddress
}: Gateway) => {
    const [error, setError] = useState<string>();

    const payXdc = async (amount: string, rate: number) => {
        const { message, status } = await pay(cardTotal, rate, receiveAddress);
        if (status === "error") {
            setError(message);
        }
    };

    return (
        <>
            <div className="font-mulish text-lg uppercase font-bold text-black py-3 ">
                Pay with DC tokens on XDC network
            </div>
            {txId ? (
                <div>
                    <Checkmark />
                    <input type="hidden" value={txId} name="xdc-datachain-gateway-txId" />
                    <div className="flex flex-col font-mulish uppercase font-bold text-black">
                        <div className="text-center my-2">Tx Hash</div>
                        <a
                            className="font-mulish text-sm text-center no-underline hover:underline my-2"
                            target="_blank"
                            rel="noreferrer"
                            href={getExplorerUrl("XDC", txId)}
                        >
                            {txId}
                        </a>
                    </div>
                </div>
            ) : (
                <>
                    <Button>
                        <img className="my-4" src={getImageUrl("xdc-dc.png")} onClick={() => payXdc(cardTotal, rate)} />
                    </Button>
                    <div className="font-mulish text-lg uppercase font-bold text-black my-4">
                        Pay {getAmountWithoutDecimals(cardTotal, rate)} XDC
                    </div>
                    {error && (
                        <div className="text-rose-600 w-1/2 font-mulish text-lg uppercase font-bold text-black my-4">
                            {error}
                        </div>
                    )}
                </>
            )}

            <Button onClick={addDcErc20Token}>Add Datachain token to the XDC Pay</Button>
        </>
    );
};

const InstallXdcPay = () => (
    <div className="font-mulish">
        Please{" "}
        <a
            className="font-mulish"
            href="https://chrome.google.com/webstore/detail/xdcpay/bocpokimicclpaiekenaeelehdjllofo"
        >
            install
        </a>{" "}
        and login to XDC Pay
    </div>
);

const Button = ({ children, onClick }: { children: React.ReactNode; onClick?: () => {} }) => {
    return (
        <div
            onClick={onClick}
            className="font-mulish w-1/2 text-center cursor-pointer m-3 p-4 text-base font-bold text-gray-900 bg-gray-50 rounded-lg hover:bg-gray-100 group hover:shadow dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-white font-mulish text-sm uppercase font-bold text-black"
        >
            {children}
        </div>
    );
};
